import { useGT } from 'gt-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

/** A Report's optional title and description, shown above its grid. */
export type WorkbookReportHeadingValue = {
  title?: string;
  description?: string;
};

/**
 * A Report's title and description above its grid: read-only (nothing when
 * both are blank), or edited in place while the Report is being laid out.
 */
export function WorkbookReportHeading({
  heading,
  onChange,
}: {
  heading: WorkbookReportHeadingValue;
  /** Absent = read-only. */
  onChange?: (
    heading: WorkbookReportHeadingValue,
    field: keyof WorkbookReportHeadingValue,
  ) => void;
}) {
  const gt = useGT();
  const title = heading.title?.trim();
  const description = heading.description?.trim();
  if (!onChange) {
    if (!title && !description) return null;
    return (
      <header className="space-y-0.5">
        {title && <h2 className="text-lg font-semibold">{title}</h2>}
        {description && (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">
            {description}
          </p>
        )}
      </header>
    );
  }
  return (
    <header className="space-y-1">
      <Input
        value={heading.title ?? ''}
        onChange={(e) =>
          onChange({ ...heading, title: e.target.value }, 'title')
        }
        maxLength={200}
        aria-label={gt('Report title', { context: 'workbook report heading' })}
        placeholder={gt('Report title (optional)', {
          context: 'workbook report heading placeholder',
        })}
        className="h-9 border-transparent px-2 text-lg font-semibold shadow-none hover:border-input focus-visible:border-input"
      />
      <Textarea
        value={heading.description ?? ''}
        onChange={(e) =>
          onChange({ ...heading, description: e.target.value }, 'description')
        }
        maxLength={2000}
        rows={1}
        aria-label={gt('Report description', {
          context: 'workbook report heading',
        })}
        placeholder={gt('Description (optional)', {
          context: 'workbook report heading placeholder',
        })}
        className="min-h-8 resize-y border-transparent px-2 py-1 text-sm text-muted-foreground shadow-none hover:border-input focus-visible:border-input"
      />
    </header>
  );
}
